import { expansionTests } from './debug-expansion.js';
import { freshDiscovery } from './discovery-state.js';
import { amount, assignSlot } from './crafting.js';
import { B, BLOCKS } from './blocks.js';
import { identity } from './identity.js';
import { fogState } from './fog.js';
// In-browser test harness (window.__test). Drives the real game logic in fixed 60 Hz steps,
// so automated play-throughs work even when the browser isn't drawing frames.
import * as THREE from 'three';
import { BrainGate, LuckyBlock, MovingPlatform, Checkpoint, Pickup, NPC } from './entities.js';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function installTestHooks(game) {
  const T = { errors: [] };
  expansionTests(T,game);
  window.addEventListener('error', (e) => T.errors.push(`${e.message} @ ${(e.filename || '').split('/').pop()}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => T.errors.push('rejection: ' + (e.reason && e.reason.stack ? e.reason.stack.split('\n').slice(0, 3).join(' | ') : e.reason)));

  // advance n frames; held = {moveX, moveY, jumpHeld}; pressed = keys pressed on the first frame
  T.step = (n = 1, held = {}, pressed = []) => {
    game.testHold = true;
    for (let i = 0; i < n; i++) {
      game.testInput = { held: { moveX: 0, moveY: 0, jumpHeld: false, ...held }, pressed: i === 0 ? pressed.slice() : [] };
      game.tick(1 / 60, false);
    }
    game.testInput = null;
  };
  T.render = () => {
    game.composer.render(1 / 60);
    return 'rendered';
  };
  T.release = () => {
    game.testHold = false;
    game.last = performance.now();
  };
  T.L = () => game.level;

  T.status = () => {
    const L = game.level;
    const s = { state: game.state, screen: game.ui.screen, quiz: game.quiz.open, errors: T.errors.slice(-5) };
    if (L) {
      const p = L.player.pos;
      Object.assign(s, { lstate: L.state, pos: [+p.x.toFixed(2), +p.y.toFixed(2), +p.z.toFixed(2)], ground: L.player.onGround, hearts: L.player.hearts, coins: L.stats.coins, gems: L.stats.gems, asked: L.stats.asked, firstTry: L.stats.firstTry, secret: L.stats.secret, falls: L.stats.falls, mobs: L.mobs.length, ents: L.entities.length });
    }
    return s;
  };

  T.boot = async () => {
    // Scenarios start signed out on purpose; multiplayer tests sign in with T.loginAs.
    if (game.accounts) game.accounts.skipped = true;
    for (let i = 0; i < 300 && !document.querySelector('.start-btn') && ['loading','boot'].includes(game.state); i++) await sleep(50);
    const sb = document.querySelector('.start-btn');
    if (sb) sb.click();
    for (let i = 0; i < 40 && game.state !== 'menu'; i++) await sleep(50);
    for (let i = 0; i < 12; i++) {
      const n = document.querySelector('[data-act="introNext"]');
      if (n) n.click();
      await sleep(60);
    }
    await sleep(800);
    game.ui.closeModal();
    return game.state;
  };

  T.tp = (x, y, z, facing) => {
    const pl = game.level ? game.level.player : game.explore ? game.explore.player : game.blitz.player;
    pl.pos.set(x, y, z);
    pl.vel.set(0, 0, 0);
    pl.platform = null;
    pl.dead = false;
    if (facing !== undefined) pl.facing = facing;
    T.step(2);
  };

  T.startLevel = async (id) => {
    game.testHold = true;
    const p = game.startLevel(id);
    for (let i = 0; i < 120 && !(game.level && game.level.id === id && game.level.state === 'intro'); i++) await sleep(25);
    if (!game.level || game.level.state !== 'intro') return 'level did not reach intro: ' + JSON.stringify(T.status());
    game.level.skipIntro();
    await p;
    game.camRig.yaw = game.level.spec.spawn.yaw;
    return game.level.state;
  };

  T.answer = async (correct = true) => {
    const Q = game.quiz;
    for (let i = 0; i < 60 && !Q.answerHandler; i++) await sleep(25);
    if (!Q.answerHandler) return false;
    const idx = correct ? Q.order.indexOf(Q.q.answer) : Q.order.findIndex((c) => c !== Q.q.answer);
    Q.answerHandler(idx);
    return true;
  };

  // answer everything in the open quiz; wrongFirst = make the very first answer wrong
  T.answerAll = async (wrongFirst = false) => {
    const Q = game.quiz;
    let n = 0;
    while (Q.open && n < 12) {
      n++;
      if (wrongFirst && n === 1) {
        await T.answer(false);
        await sleep(1000);
        const ok = document.querySelector('.fb-ok');
        if (ok) ok.click();
        await sleep(60);
      }
      if (!(await T.answer(true))) break;
      document.querySelector('.quiz-next')?.click();
      for (let k = 0; k < 24; k++) {
        T.step(3);
        await sleep(50);
        if (!Q.open || Q.answerHandler) break;
      }
    }
    return !Q.open;
  };

  T.playLevel = async (id, { wrongFirst = true } = {}) => {
    const rep = { id, pass: [], fail: [] };
    const ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    const st = await T.startLevel(id);
    const L = game.level;
    ok(st === 'play', 'level starts (intro skipped): ' + st);
    if (!L || L.state !== 'play') return rep;
    game.camRig.yaw = 0;
    T.step(30);
    ok(L.player.onGround, 'Sai stands on the spawn island');
    const z0 = L.player.pos.z;
    T.step(30, { moveY: 1 });
    ok(L.player.pos.z > z0 + 1.5, 'walking forward moves Sai');
    const y0 = L.player.pos.y;
    T.step(1, { jumpHeld: true }, ['jump']);
    T.step(12, { jumpHeld: true });
    ok(L.player.pos.y > y0 + 0.8, 'jumping works');
    T.step(40);
    // checkpoints
    for (const cp of L.entities.filter((e) => e instanceof Checkpoint && !e.start)) {
      T.tp(cp.pos.x, cp.pos.y + 0.2, cp.pos.z);
      T.step(5);
      ok(cp.active && L.checkpoint === cp, `checkpoint at z=${cp.pos.z.toFixed(0)} activates`);
    }
    // respawn after a fall
    const cp = L.checkpoint;
    T.tp(cp.pos.x, L.killY - 1, cp.pos.z);
    await sleep(800);
    T.step(5);
    ok(!L.player.dead && L.player.pos.distanceTo(cp.pos) < 1.5, 'falling respawns Sai at the last checkpoint');
    // gates (in course order)
    const gates = L.entities.filter((e) => e instanceof BrainGate).sort((a, b) => a.trigger.z - b.trigger.z);
    let gi = 0;
    for (const g of gates) {
      gi++;
      T.tp(g.trigger.x, g.trigger.y + 0.1, g.trigger.z - 0.8, 0);
      T.step(4);
      // A wandering monster can move between Sai and the gate. The real action
      // prioritises a swing, so clear that obstruction before asking to solve.
      for(let attempt=0;attempt<8 && L.facingHittable() && !g.solved;attempt++) {
        T.step(1,{},['action']); T.step(40);
        if(game.quiz.open)await T.answerAll();
        T.tp(g.trigger.x,g.trigger.y+.1,g.trigger.z-.8,0); T.step(4);
      }
      ok(!!L.lastOffer, `gate ${gi}: "Fix the bridge" prompt appears`);
      ok(document.getElementById('tbtn-action').textContent.includes(L.facingHittable()?'HIT':'SOLVE'), `gate ${gi}: action label matches its behaviour`);
      T.step(1, {}, ['action']);
      await sleep(40);
      ok(game.quiz.open, `gate ${gi}: quiz opens`);
      const done = await T.answerAll(wrongFirst && gi === 1);
      ok(done, `gate ${gi}: quiz finishes after correct answers`);
      T.step(120);
      const built = g.blocks.every((b) => L.world.get(b.x, b.y, b.z) === b.id);
      ok(built && g.solved, `gate ${gi}: bridge rebuilt (${g.blocks.length} blocks)`);
      const zEnd = Math.max(...g.blocks.map((b) => b.z)) + 1.6;
      const falls = L.stats.falls;
      game.camRig.yaw = 0;
      T.tp(g.trigger.x, g.trigger.y + 0.1, g.trigger.z - 0.5, 0);
      for (let k = 0; k < 300 && L.player.pos.z < zEnd; k++) T.step(1, { moveY: 1 });
      ok(L.player.pos.z >= zEnd && L.stats.falls === falls, `gate ${gi}: Sai walks across the new bridge`);
    }
    // lucky blocks
    for (const lb of L.entities.filter((e) => e instanceof LuckyBlock && !e.brainBlock)) {
      T.tp(lb.center.x, lb.center.y - 0.5, lb.center.z - 1.5, 0);
      T.step(1, {}, ['action']);
      T.step(30);
      ok(lb.dead, `lucky block at z=${lb.center.z.toFixed(0)} breaks when hit`);
      T.step(120);
    }
    // brain blocks
    for (const bb of L.entities.filter((e) => e instanceof LuckyBlock && e.brainBlock)) {
      T.tp(bb.center.x, bb.center.y - 0.5, bb.center.z - 1.6, 0);
      T.step(3);
      T.step(1, {}, ['action']);
      T.step(30);
      await sleep(40);
      ok(game.quiz.open, 'brain block opens a question');
      await T.answerAll(false);
      T.step(10);
      ok(bb.dead, 'brain block breaks after the right answer');
      T.step(120);
    }
    // mobs: bonk the first two
    for (const m of L.mobs.slice(0, 2)) {
      const kind = m.kind;
      for (let k = 0; k < 6 && !m.dying && !m.dead; k++) {
        const p = m.body.pos;
        T.tp(p.x, p.y + (m.flyer ? -0.8 : 0.05), p.z - 1.3, 0);
        L.player.invuln = 5;
        T.step(1, {}, ['action']);
        T.step(26);
      }
      ok(m.dying || m.dead, `${kind} defeated with the sword`);
    }
    // moving platform ride
    const mp = L.entities.find((e) => e instanceof MovingPlatform);
    if (mp) {
      T.tp(mp.pos.x + mp.size.x / 2, mp.pos.y + 1.02, mp.pos.z + mp.size.z / 2);
      const start = L.player.pos.clone();
      T.step(90);
      ok(L.player.platform === mp.col && L.player.pos.distanceTo(start) > 0.5 && !L.player.dead, 'Sai rides a moving platform');
    }
    // secret star
    const star = L.entities.find((e) => e instanceof Pickup && e.kind === 'star');
    if (star) {
      T.tp(star.obj.position.x, star.obj.position.y - 0.9, star.obj.position.z);
      T.step(3);
      ok(L.stats.secret, 'secret star collects');
    }
    ok(L.stats.coins > 0, `coins collected (${L.stats.coins})`);
    // finish
    const portal = L.portal;
    T.tp(portal.center.x, portal.center.y - 1.6, portal.center.z, 0);
    T.step(3);
    ok(L.state === 'win', 'entering the portal finishes the level');
    await sleep(2900);
    T.step(2);
    ok(game.state === 'results' && game.ui.screen === 'results', 'results screen shows');
    rep.stats = T.status();
    rep.errors = T.errors.slice();
    return rep;
  };

  T.playBoss = async (id) => {
    const rep = { id, pass: [], fail: [] };
    const ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    const st = await T.startLevel(id);
    const L = game.level, B = L.boss;
    ok(st === 'play' && !!B, 'boss level starts');
    let rounds = 0, wrongTried = false;
    while (B.hp > 0 && rounds++ < 12) {
      for (let k = 0; k < 3600 && B.state !== 'dizzy'; k++) {
        L.player.invuln = 5;
        T.step(1);
      }
      if (B.state !== 'dizzy') {
        ok(false, `boss gets dizzy (stuck in ${B.state})`);
        break;
      }
      const hit = (i) => {
        const pil = B.pillars[i];
        const d = new THREE.Vector3(0.5 - pil.pos.x, 0, 0.5 - pil.pos.z).normalize();
        T.tp(pil.pos.x + d.x * 1.7, 41.02, pil.pos.z + d.z * 1.7, Math.atan2(-d.x, -d.z));
        L.player.invuln = 5;
        T.step(1, {}, ['action']);
        T.step(24);
      };
      if (!wrongTried) {
        wrongTried = true;
        const wrong = [0, 1, 2, 3].find((i) => i !== B.answerIdx);
        const hp = B.hp;
        hit(wrong);
        ok(B.hp === hp && B.pillars[wrong].disabled, 'wrong pillar does no damage and shows the answer');
      }
      const hp = B.hp;
      hit(B.answerIdx);
      ok(B.hp === hp - 1, `right pillar zaps the boss (hp ${B.hp}/${B.maxHp})`);
      T.step(100);
    }
    ok(B.hp <= 0, 'boss defeated');
    for (let k = 0; k < 10; k++) {
      T.step(30);
      await sleep(250);
    }
    ok(L.portal.active, 'portal opens after the boss is beaten');
    T.tp(L.portal.center.x, L.portal.center.y - 1.6, L.portal.center.z, 0);
    T.step(3);
    ok(L.state === 'win', 'portal finishes the boss level');
    await sleep(2900);
    ok(game.state === 'results', 'results screen shows');
    rep.errors = T.errors.slice();
    return rep;
  };

  T.playBlitz = async (mode = 'tables') => {
    const rep = { mode, pass: [], fail: [] };
    const ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    game.testHold = true;
    const p = game.startBlitz(mode);
    await sleep(450);
    await p;
    const Z = game.blitz;
    ok(!!Z, 'blitz starts');
    T.step(170);
    ok(Z.state === 'play' && !!Z.q, 'countdown finishes and a question appears');
    const answersVisible=()=>Z.blocks.every(b=>{const p=b.label.getWorldPosition(new THREE.Vector3()).project(game.camera);return Math.abs(p.x)<.88 && Math.abs(p.y)<.8 && p.z<1;});
    ok(answersVisible(),'all four Blitz answers fit in the overview camera');
    for (let i = 0; i < 6; i++) {
      Z.startDash(Z.answerIdx);
      T.step(16);
      await sleep(420);
      T.step(2);
    }
    ok(answersVisible(),'answer signs stay visible after dashing');
    ok(Z.correct === 6 && Z.score > 0, `right answers score points (${Z.correct} right, ${Z.score} pts)`);
    const wrong = [0, 1, 2, 3].find((i) => i !== Z.answerIdx);
    const t0 = Z.timeLeft;
    Z.startDash(wrong);
    T.step(16);
    ok(Z.combo === 0 && Z.timeLeft < t0 - 2, 'wrong answer resets combo and costs time');
    Z.timeLeft = 0.05;
    T.step(4);
    ok(Z.state === 'over', 'time runs out');
    await sleep(2100);
    ok(game.state === 'results', 'blitz results screen');
    rep.errors = T.errors.slice();
    return rep;
  };

  T.uiTour = async () => {
    const rep = { pass: [], fail: [] };
    const ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    const click = async (sel) => {
      const el = document.querySelector(sel);
      if (el) el.click();
      await sleep(120);
      return !!el;
    };
    T.release();
    game.showMenu();
    await sleep(200);
    ok(await click('[data-act="play"]'), 'PLAY button');
    ok(game.ui.screen === 'worlds', 'world select opens');
    ok(await click('[data-act="wnext"]'), 'next world arrow');
    ok(await click('[data-act="wprev"]'), 'previous world arrow');
    ok(await click('[data-act="home"]'), 'back to menu');
    game.save.data.coins += 3000;
    ok(await click('[data-act="locker"]'), 'locker opens');
    ok(await click('[data-act="item"][data-cat="skin"][data-id="hoodie"]'), 'pick hoodie');
    await sleep(150);
    ok(await click('[data-act="confirmYes"]'), 'confirm purchase');
    await sleep(200);
    ok(game.save.data.owned.skin.includes('hoodie') && game.save.data.equip.skin === 'hoodie', 'hoodie bought and worn');
    ok(await click('[data-act="ltab"][data-tab="pet"]'), 'pets tab');
    ok(await click('[data-act="item"][data-cat="pet"][data-id="slime"]'), 'pick slime pet');
    await sleep(150);
    await click('[data-act="confirmYes"]');
    await sleep(200);
    ok(game.save.data.equip.pet === 'slime', 'pet adopted');
    ok(await click('[data-act="ltab"][data-tab="look"]'), 'look tab');
    ok(await click('[data-act="tone"][data-i="1"]'), 'change skin tone');
    ok(await click('[data-act="home"]'), 'home');
    ok(await click('[data-act="badges"]'), 'badges');
    ok(document.querySelectorAll('.badge').length > 20, 'badges listed');
    ok(await click('[data-act="home"]'), 'home');
    ok(await click('[data-act="stats"]'), 'brain stats');
    ok(document.querySelectorAll('.tt-c').length === 132, 'times-table grid');
    ok(await click('[data-act="home"]'), 'home');
    ok(await click('[data-act="settings"]'), 'settings');
    ok(!!document.querySelector('.save-code'), 'save code shown');
    // Vibration: the switch is saved both ways, and switching it on gives one little buzz.
    for (let i = 0; i < 100 && game.badgeTimer; i++) await sleep(100); // a badge buzz could rate-limit this one
    const buzzes = [], vibration = !!game.save.data.settings.haptics;
    navigator.vibrate = (pattern) => (buzzes.push(pattern), true);
    ok(document.querySelector('[data-k="haptics"]')?.classList.contains('on') === vibration, 'vibration switch in Settings');
    for (const on of [!vibration, vibration]) {
      ok(await click('[data-k="haptics"]') && game.save.data.settings.haptics === on && JSON.parse(localStorage.getItem('saicraft.save.v1')).settings.haptics === on, `vibration ${on ? 'on' : 'off'} is saved`);
    }
    delete navigator.vibrate;
    ok(buzzes.length === 1 && buzzes[0].join() === '12,40,20', `switching vibration on buzzes once (${JSON.stringify(buzzes)})`);
    ok(await click('[data-act="home"]'), 'home');
    ok(await click('[data-act="friends"]'), 'friends screen');
    ok(!!document.querySelector('[data-act="whoStart"]'), 'signed-out players are offered a player card');
    const who = game.accounts.who({ canSkip: true });
    ok(!!document.querySelector('.who-name') && document.querySelectorAll('.who-month option[value]:not([value=""])').length === 12, 'who’s playing asks for a name and birthday month');
    ok(await click('[data-act="whoLogin"]'), 'sign-in without a birthday');
    ok(!!document.querySelector('.who-error')?.textContent.trim(), 'a kind message explains what is missing');
    ok(await click('[data-act="whoSkip"]'), 'play on this device for now');
    await who;
    game.showMenu();
    await sleep(100);
    ok(await click('[data-act="blitz"]'), 'blitz menu');
    ok(!!document.querySelector('.blitz-mode'), 'blitz modes listed');
    game.ui.closeModal();
    rep.errors = T.errors.slice();
    return rep;
  };

  // Friends sign in with their own names: everything Ava reads says Ava, never Sai.
  T.playerNames = async () => {
    const rep = { pass: [], fail: [] };
    const ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    const previous = JSON.parse(JSON.stringify(game.save.data));
    const ui = game.ui, sai = /\bSai\b|\bSAI\b/;
    const says = (el) => (el?.textContent || '').replace('made especially for Sai', '');
    try {
      game.save.data.name = 'Ava';
      T.release();
      game.showMenu();
      await sleep(150);
      const greetings = [];
      for (let i = 0; i < 30; i++) {
        ui.showMain();
        greetings.push(says(ui.screenEl.querySelector('.tagline')));
      }
      ok(greetings.some((g) => g.includes('Ava')) && !greetings.some((g) => sai.test(g)), 'menu greetings use the player’s name');
      ok(says(ui.screenEl.querySelector('.pc-name')).startsWith('AVA'), 'player card');
      ui.showLocker('skin');
      const locker = says(ui.screenEl);
      ok(locker.includes("AVA'S LOCKER") && locker.includes('Classic Ava') && locker.includes('Mecha Ava') && locker.includes('Drag to spin Ava!') && !sai.test(locker), 'locker title, skins and hint');
      ui.showLocker('look');
      ok(says(ui.screenEl).includes('Make Ava look just like you!'), 'look tab hint');
      ui.showStats();
      ok(says(ui.screenEl).includes("AVA'S BRAIN STATS"), 'brain stats title');
      ui.showSettings();
      ok(says(ui.screenEl).includes('“Auto” adapts to Ava') && ui.screenEl.textContent.includes('made especially for Sai'), 'settings hint names the player; the dedication stays');
      ui.showDaily();
      ok(says(ui.modalEl).includes('bigger rewards, Ava!'), 'daily reward');
      ui.closeModal();
      ui.showLevelUp(100, true, 0);
      ok(says(ui.modalEl).includes('NEW RANK: AVA SUPREME'), 'top rank carries the name');
      ui.closeModal();
      ui.showLevelUp(101, false, 0);
      ok(says(ui.modalEl).includes('You are the AVA SUPREME!'), 'top rank reminder');
      ui.closeModal();
      const reset = ui.act('reset', {});
      await sleep(50);
      ok(says(ui.modalEl).includes('ALL of Ava’s progress'), 'reset warning names the player');
      ui.modalEl.querySelector('[data-act="confirmNo"]')?.click();
      await reset;
      const quiz = game.quiz.run({ needed: 1, weights: { word: 1 } });
      await sleep(100);
      const question = says(document.querySelector('.quiz-q'));
      await T.answer(false);
      await sleep(100);
      const encourage = says(document.querySelector('.quiz-feedback .fb-big'));
      await sleep(1000);
      document.querySelector('.fb-ok')?.click();
      await sleep(100);
      await T.answer(true);
      await sleep(100);
      const praise = says(document.querySelector('.quiz-feedback .fb-big'));
      await quiz;
      ok(question && encourage && praise && ![question, encourage, praise].some((t) => sai.test(t)), 'word problem and quiz feedback');
      ok((await T.startLevel('1-1')) === 'play', 'tutorial starts');
      const lines = game.level.entities.filter((e) => e instanceof NPC).map((e) => e.text);
      ok(lines.length > 0 && lines.every((t) => !sai.test(t) && !t.includes('{name}')) && lines.some((t) => t.includes('Ava')), 'Adventure guides greet the player');
      game.quitLevel();
      await game.startExplore();
      T.step(5);
      const E = game.explore, X = E.expansion, d = E.data;
      ok(E.entities.find((e) => e instanceof NPC)?.text.startsWith('Hi Ava!'), 'Explore guide greets the player');
      E.openInventory('projects');
      const bag = says(ui.modalEl);
      ok(bag.includes('AVA’S BACKPACK') && bag.includes('Ava’s jump park') && !sai.test(bag), 'backpack and projects');
      game.resume();
      ok(X.homeSign() === 'Ava’s home', 'unwritten signs say whose home it is');
      Object.assign(d, { built: true, dragonHatched: true });
      Object.assign(d.expansion.flags, { cave: true, flightLesson: true, flew: true, sky: true, circuit: true, railRide: true });
      d.expansion.harvests = 3;
      d.blocks.push([30, 41, 30, B.PERCH]);
      d.inventory.items.saddle = 1;
      E.moved = true;
      ok(E.guide().title === 'Ava’s world is yours to invent', 'last quest names the player');
      X.marker('sky');
      ok(says(ui.modalEl).includes('Ava’s Sky Garden'), 'sky garden belongs to the world’s owner');
      X.close();
      game.eyeHelpShown = false;
      document.querySelector('#toasts').replaceChildren();
      game.camRig.setDistance(0);
      T.step(3);
      ok(says(document.querySelector('#toasts')).includes('zoom out to see Ava again'), 'eye view tip');
      game.camRig.setDistance(7.5);
      T.step(3);
    } catch (e) {
      T.errors.push(e.stack || String(e));
    } finally {
      game.showMenu();
      game.save.data = previous;
      game.save.persist();
      game.refreshCharacter();
    }
    rep.errors = T.errors.slice();
    return rep;
  };

  T.playExplore = async () => {
    const rep = { id: 'explore', pass: [], fail: [] }, ok = (c, m) => (c ? rep.pass : rep.fail).push(m);
    game.testHold = true;
    await game.startExplore(); T.step(30);
    let E = game.explore;
    ok(E.player.onGround, 'explorer spawns safely');
    ok(E.pet.kind === game.save.data.equip.pet, 'equipped pet follows explorer');
    ok(E.stations.filter(s=>!s.topicId).length === 6, 'six home learning stations');
    ok(E.guide().title.includes('steps'), 'first visit teaches movement before wider goals');
    const start = E.player.pos.z; T.step(25, { moveY: 1 });
    ok(E.player.pos.z > start + 1, 'free movement');
    const dist = game.camRig.dist; T.step(1, {}, ['+']);
    ok(game.camRig.dist === Math.max(3.2, dist - 1), 'keyboard zoom');
    T.step(1, {}, ['0']); ok(game.camRig.dist === 7.5, 'reset camera zoom');
    const canvas = game.input.canvas, input = game.input;
    const pointer = (type, id, x, y) => canvas.dispatchEvent(new PointerEvent(type, { pointerId: id, pointerType: 'touch', clientX: x, clientY: y, bubbles: true }));
    const cx = window.innerWidth * 0.65, cy = window.innerHeight * 0.55;
    pointer('pointerdown', 21, cx, cy); pointer('pointerdown', 22, cx + 60, cy);
    pointer('pointermove', 22, cx + 140, cy); T.step(1);
    ok(game.camRig.dist < 7.5 && input.camDX === 0, 'pinch apart zooms in without rotating');
    const nearDist = game.camRig.dist;
    pointer('pointermove', 22, cx + 30, cy); T.step(1);
    ok(game.camRig.dist > nearDist, 'pinch together zooms out');
    pointer('pointercancel', 22, cx + 30, cy); pointer('pointerup', 21, cx, cy); T.step(1);
    ok(input.cameraTouches.size === 0 && !game.quiz.open, 'pinch cancellation clears touches without an accidental action');
    {
      // Explore fogs by flat distance from the player (fog.js). With the fog turned magenta, the loaded world's edge
      // reads fully fogged from Ember's height, zoomed out on a wide screen (the PR 7 review view), and the ground
      // near the player stays clear. Pixels are read straight after the render.
      const fog = game.scene.fog, colour = fog.color.clone(), rig = game.camRig,
        keep = { yaw: rig.yaw, pitch: rig.pitch, dist: rig.dist, distCur: rig.distCur };
      ok(fog.near === 38 && fog.far === 62 && fogState().flat, 'Explore fog: 38 blocks clear all round the player, complete by 62');
      game.applyTheme('plains');
      ok(!fogState().flat, 'other modes keep camera-depth fog');
      E.updateFog();
      fog.color.set('#ff00ff');
      E.stream.update(31.5, 31.5, true);
      E.player.pos.set(31.5, 70, 31.5);
      Object.assign(rig, { yaw: 0, pitch: 0.7, dist: 18, distCur: 18 });
      rig.focus.set(31.5, 71.45, 31.5);
      rig.apply(null, 0);
      E.updateFog();
      T.render();
      const gl = game.renderer.getContext(), cam = game.camera, top = (x, z) => { for (let y = 70; y > 8; y--) { const b = BLOCKS[E.world.get(x, y, z)]; if (b?.solid && b.opaque) return y + 1; } return null; },
        pixel = (x, y, z) => {
          const v = new THREE.Vector3(x, y, z).project(cam);
          if (Math.abs(v.x) > 0.98 || Math.abs(v.y) > 0.98 || v.z > 1) return null;
          const to = new THREE.Vector3(x, y, z).sub(cam.position), d = to.length(); to.normalize();
          const hit = E.world.raycast(cam.position.x, cam.position.y, cam.position.z, to.x, to.y, to.z, d - 0.3, (id) => id);
          if (hit) return null; // something nearer hides it
          const out = new Uint8Array(4);
          gl.readPixels(Math.round(((v.x + 1) / 2) * gl.drawingBufferWidth), Math.round(((v.y + 1) / 2) * gl.drawingBufferHeight), 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, out);
          return out;
        },
        magenta = (c) => c[0] > 200 && c[1] < 70 && c[2] > 200;
      const edge = [], near = [];
      // Centres of the last loaded blocks on the side (x) and far (z) edges, 64+ blocks from the player. (Points
      // right on the cut would be smoothed into the sky behind them.)
      for (let n = 2; n <= 92; n += 6)
        for (const [x, z] of [[95, n], [n, 95]]) { const y = top(x, z); if (y) { const c = pixel(x + 0.5, y + 0.02, z + 0.5); if (c) edge.push(c); } }
      for (const [x, z] of [[40, 50], [45, 55], [35, 58]]) { const y = top(x, z); if (y) { const c = pixel(x + 0.5, y + 0.02, z + 0.5); if (c) near.push(c); } }
      ok(edge.length >= 3 && edge.every(magenta), `the world's edge is fully fogged from Ember's view (${edge.length} points${edge.filter((c) => !magenta(c)).map((c) => ` ${[...c]}`).join('')})`);
      ok(near.length >= 1 && !near.some(magenta), `ground near the player stays clear (${near.length} points)`);
      fog.color.copy(colour);
      Object.assign(rig, keep);
    }
    E.goHome(); T.step(1, {}, ['b']); T.tp(0.5, 41, 4.5);
    const supplies = E.data.supplies;
    ok(E.placeBlock([2, 41, 5]), 'place a block on the golden plot');
    ok(E.data.supplies === supplies - 1, 'building uses one earned block');
    ok(!E.placeBlock([0, 41, 4]), 'cannot trap player inside a block');
    ok(!E.placeBlock([-12, 41, 0]), 'cannot build over learning stations');
    game.pause(); ok(!E.placeBlock([3, 41, 5]), 'pause blocks building mutations'); game.resume();
    ok(E.removeBlock([2, 41, 5]) && E.data.supplies === supplies, 'removing refunds the block');
    ok(!E.removeBlock([0, 40, 4]), 'instant Remove only refunds player-built blocks');
    E.placeBlock([2, 41, 5]); game.save.persist();
    await game.startExplore(); E = game.explore; T.step(2);
    ok(E.world.get(2, 41, 5) !== 0, 'build survives leaving and returning');
    E.building = false;
    // Lesson rewards (1.7) pop up one after another, and the toast area keeps only three, so record every toast.
    const toasts = [], realToast = game.ui.toast;
    game.ui.toast = (html, ...rest) => { toasts.push(String(html)); return realToast.call(game.ui, html, ...rest); };
    const toastSaid = async (text, ms = 4000) => { for (let t = 0; t < ms && !toasts.some((h) => h.includes(text)); t += 50) await sleep(50); return toasts.some((h) => h.includes(text)); };
    // Start without the Builder's gift, so the Builder lesson can show it arriving once.
    delete E.data.stamps.builder; delete E.data.inventory.items.bow;
    E.data.inventory.hotbar = E.data.inventory.hotbar.map((x) => (x === 'bow' ? null : x)); E.data.expansion.flags.bowGift = false;
    if (!E.data.inventory.hotbar.includes(null)) E.data.inventory.hotbar[7] = null;
    const arrowsBefore = amount(E.data, 'arrow');
    for (let i = 0; i < 6; i++) {
      const s = E.stations[i]; T.tp(s.x + 2.5, 41, s.z + 0.5);
      toasts.length = 0;
      const pending = E.learn(s.id); await sleep(30);
      ok(game.quiz.open, s.name + ': quiz opens');
      if (i === 0) {
        const q = game.quiz, askedBefore = game.save.data.stats.answered;
        q.$('.quiz-hint').click(); ok(!!q.$('.quiz-hint-text').textContent, 'a helpful hint is available');
        await T.answer(true); await sleep(40);
        ok(!q.answerHandler && q.open && !!q.$('.quiz-next'), 'correct answer waits for the learner to continue');
        ok(game.save.data.stats.answered === askedBefore + 1, 'assisted answers count once');
        q.$('.quiz-next').click();
      }
      if (i === 2) {
        // A warm-up, one at your level, then the labelled 🔥 challenge.
        const q = game.quiz, panel = q.$('.quiz-panel'), wonBefore = game.save.data.stats.challenges || 0;
        for (let k = 0; k < 2; k++) {
          ok(!panel.classList.contains('challenge') && !q.q.challenge, `lesson question ${k + 1} is not the challenge`);
          await T.answer(true); await sleep(40); q.$('.quiz-next').click(); await sleep(40);
        }
        ok(panel.classList.contains('challenge') && q.q.challenge && q.$('.quiz-prompt').textContent.includes('Challenge'), 'the third lesson question is the labelled challenge');
        await T.answer(true); await sleep(40);
        ok((game.save.data.stats.challenges || 0) === wonBefore + 1, 'winning the challenge first time counts in stats.challenges');
        q.$('.quiz-next').click();
      }
      await T.answerAll(i === 1); await pending;
      ok(E.data.stamps[s.id] >= 1, s.name + ': stamp earned');
      ok(E.state === 'play' && !E.player.frozen, s.name + ': returns to exploring');
      if (i === 2) {
        ok(await toastSaid('chest'), 'a finished lesson opens a lesson chest');
        ok(await toastSaid('Challenge won'), 'the won challenge pays a gem');
      }
      if (i === 5) {
        ok(amount(E.data, 'bow') === 1 && amount(E.data, 'arrow') >= arrowsBefore + 12 && E.data.inventory.hotbar.includes('bow'), 'the first Builder stamp brings a bow and 12 arrows, ready in a slot');
        ok(await toastSaid('Builder’s gift', 6000), 'the Builder’s gift is announced'); // it waits for the main toast to go
      }
    }
    ok(E.data.dragonHatched && game.save.data.owned.pet.includes('dragon') && E.pet.kind === 'dragon', 'all discoveries hatch and equip Ember');
    {
      // Another Builder lesson, and an older save that earned the stamp before the gift: still only one bow.
      const b = E.stations[5], arrows = amount(E.data, 'arrow'); T.tp(b.x + 2.5, 41, b.z + 0.5); toasts.length = 0;
      const again = E.learn(b.id); await sleep(30); await T.answerAll(); await again; await sleep(4300);
      ok(amount(E.data, 'bow') === 1 && amount(E.data, 'arrow') < arrows + 12 && !toasts.some((h) => h.includes('Builder’s gift')), 'a second Builder lesson brings no second gift');
      delete E.data.inventory.items.bow; E.data.expansion.flags.bowGift = false;
      const older = amount(E.data, 'arrow');
      await game.startExplore(); E = game.explore; T.step(2);
      ok(amount(E.data, 'bow') === 1 && amount(E.data, 'arrow') === older + 12 && await toastSaid('Builder’s gift'), 'an older Builder stamp brings the gift when Explore opens');
      await game.startExplore(); E = game.explore; T.step(2);
      ok(amount(E.data, 'bow') === 1 && amount(E.data, 'arrow') === older + 12, 'the gift never comes twice');
      E.building = false; // the restart reselected the saved slot
    }
    delete game.ui.toast; // back to the real one
    const stampBefore = E.data.stamps.numbers;
    const s = E.stations[0]; T.tp(s.x + 2.5, 41, s.z + 0.5);
    const cancel = E.learn(s.id); game.quiz.cancel(); await cancel;
    ok(E.data.stamps.numbers === stampBefore && E.state === 'play', 'Later leaves the station without a stamp');
    E.journal(); ok(game.paused && !!document.querySelector('.journal-grid'), 'journal pauses the world');
    game.resume(); E.goHome(); T.tp(0.5, E.killY - 1, -4); T.step(2);
    ok(E.player.pos.y >= 41 && !E.player.dead, 'falls return home with no punishment');
    E.help(); ok(game.paused && !!document.querySelector('.help-steps'), 'help explains three simple steps'); game.resume();
    E.journal(); E.navigate('numbers'); T.step(1);
    ok(!game.paused && E.waypoint?.id==='numbers' && E.trail.visible, 'map choice sets a visible trail and resumes play');
    T.tp(0,41,100); T.step(20);
    ok(E.player.onGround && E.stream.loaded.size<=25, 'walkable wilderness with bounded terrain memory');
    const wild=E.stations.find(s=>s.topicId && s.z>64);
    ok(!!wild, 'new learning stops appear beyond town');
    T.tp(wild.x+2.5,41,wild.z+.5);
    const nearbyMonster=[...E.enemies.values()][0];nearbyMonster.body.pos.copy(E.player.pos);nearbyMonster.stun=0;E.player.invuln=0;
    const lesson=E.learn(wild.id); await sleep(30); ok(game.quiz.open, 'wilderness lessons open');
    await T.answerAll(); await lesson;
    ok(E.state==='play', 'wilderness learning rewards return to exploring');
    const lessonHearts=E.player.hearts;E.player.hurt(1,nearbyMonster.body.pos);
    ok(E.player.hearts===lessonHearts&&nearbyMonster.stun===3,'returning from a lesson gives time to look around before monsters attack');
    // A far build must survive unload/reload, not just a menu transition.
    T.tp(114.5,41,116.5); T.step(30); E.building=true;
    const cell=[115,41,116];
    ok(E.placeBlock(cell), 'building works beyond the starter plot');
    E.goHome(); T.step(3);
    ok(E.player.onGround && E.world.get(...cell)===0, 'Home restores town and unloads distant blocks');
    T.tp(114.5,41,116.5); T.step(30);
    ok(E.world.get(...cell)!==0, 'distant build is restored when revisiting');
    E.removeBlock(cell); E.building=false;
    T.tp(9008,41,-9008); T.step(30);
    ok(E.player.onGround && E.stream.loaded.size<=25, 'far positive and negative coordinates remain safe');
    E.goHome(); T.step(2);
    game.showMenu(); ok(!document.querySelector('#explore-hud') && !game.explore, 'leaving island cleans up the mode');
    rep.errors = T.errors.slice(); return rep;
  };

  T.playCrafting = async () => {
    const rep={id:'crafting',pass:[],fail:[]},ok=(c,m)=>(c?rep.pass:rep.fail).push(m);
    const previous=JSON.parse(JSON.stringify(game.save.data));game.showMenu();
    game.save.data.discovery=freshDiscovery();game.save.data.coins=0;game.save.data.gems=0;game.save.data.stats.correct=0;game.save.data.owned.sword=['wood'];game.save.data.equip.sword='wood';
    try {
      const buttons=[...document.querySelectorAll('.mode-card')];
      ok(buttons.length===2&&buttons[0].dataset.act==='play','Adventure is first beside optional Explore');
      ok(buttons[0].getBoundingClientRect().right<=buttons[1].getBoundingClientRect().left,'home modes appear side by side');
      const cancelledStart=game.startLevel('1-1');game.showMenu();await cancelledStart;ok(game.state==='menu'&&!game.level,'rapid mode changes cancel a pending Adventure launch');
      const introStart=game.startLevel('1-1');for(let i=0;i<30&&!game.level;i++)await sleep(25);game.showMenu();await introStart;ok(game.state==='menu'&&!document.querySelector('#go-splash.show'),'leaving the flyover cannot restart gameplay or flash GO');
      await game.startExplore();T.step(10);let E=game.explore,d=E.data;
      ok(d.inventory.hotbar.length===8&&amount(d,'axe_wood')===1&&amount(d,'pick_wood')===1,'fresh explorer receives an eight-slot bag and usable starter tools');
      ok(E.wildlife.animals.size>0&&E.wildlife.animals.size<=27,'animals stream within a bounded neighbourhood');
      E.selectSlot(3);T.step(125);const ahead=E.targetCell();ok(E.canPlace(ahead),'default building aim reaches clear ground ahead of Sai');T.step(1,{},['e']);ok(E.world.get(...ahead)===B.PLANKS&&amount(d,'planks')===23,'Place key uses the visible target and spends one block');T.step(1,{},['r']);ok(E.world.get(...ahead)===0&&amount(d,'planks')===24,'Remove key targets and refunds the placed block');
      E.selectSlot(1);T.tp(12.5,41,-19.5);T.step(15);
      const woodBefore=amount(d,'wood');ok(E.beginGather([10,41,-20]),'axe begins chopping a real tree trunk');T.step(80);
      ok(E.world.get(10,41,-20)!==0&&amount(d,'wood')===woodBefore,'wood does not arrive before the chopping timer');
      T.step(1,{moveX:1});ok(!E.mining&&amount(d,'wood')===woodBefore,'walking cancels gathering without a reward');T.step(20);
      ok(E.beginGather([10,41,-20]),'chopping can be restarted');T.step(185);
      ok(E.world.get(10,41,-20)===0&&amount(d,'wood')===woodBefore+3,'completed chopping removes a block and awards wood once');
      E.beginGather([10,42,-20]);T.step(185);ok(amount(d,'wood')===woodBefore+6,'higher tree blocks are collectable');
      E.selectSlot(2);T.tp(9.5,41,-9.5);T.step(10);const stoneBefore=amount(d,'stone');
      ok(E.beginGather([9,41,-12]),'pickaxe begins mining a real stone outcrop');T.step(80);game.pause();ok(!E.mining,'pause cancels gathering');game.resume();
      E.beginGather([9,41,-12]);E.journal();ok(!E.mining,'opening the map cancels gathering');game.resume();E.beginGather([9,41,-12]);E.help();ok(!E.mining,'opening help cancels gathering');game.resume();E.beginGather([9,41,-12]);T.step(245);ok(amount(d,'stone')===stoneBefore+3,'stone takes longer and awards its advertised quantity');
      ok(!E.beginGather([13,41,-22]),'hard ore rejects an insufficient or out-of-range pickaxe');
      ok(!E.canHarvest([-12,41,0])&&!E.canHarvest([0,37,0]),'learning spaces and the world floor are protected');
      E.openInventory('craft','special');ok(game.paused&&!!document.querySelector('.inventory-panel'),'inventory pauses the world and explains crafting');
      const materials=JSON.stringify(d.inventory.items);ok(!E.craftItem('pick_ruby')&&JSON.stringify(d.inventory.items)===materials,'locked crafting cannot spend materials');
      ok(E.craftItem('table')&&amount(d,'workbench')===1&&amount(d,'wood')===2&&amount(d,'stone')===1,'a portable table uses exactly four wood and two stone');
      E.openInventory('bag');E.chooseInventoryItem('workbench');E.chooseInventorySlot(4);ok(d.inventory.hotbar[4]==='workbench'&&d.inventory.slot===4,'inventory item is assigned to a chosen hotbar slot');
      game.resume();E.goHome();T.tp(.5,41,4.5);T.step(10);ok(E.placeBlock([2,41,5]),'held crafting table places on a block face');
      ok(amount(d,'workbench')===0&&E.nearBench(),'placed table becomes a usable crafting station');
      ok(!E.placeBlock([3,41,5]),'an empty stack cannot create another table');
      game.save.data.coins=100;E.openInventory('shop');const money=game.save.data.coins;
      ok(E.craftItem('timber',true)&&game.save.data.coins===money-60&&amount(d,'wood')===10,'trade converts shared coins into the displayed wood bundle');
      const poor=game.save.data.coins;ok(!E.craftItem('diamond',true)&&game.save.data.coins===poor,'locked and unaffordable trades leave the wallet untouched');
      game.resume();E.goHome();E.openInventory('shop');ok(!E.craftItem('stone',true),'trade cannot be used away from a table');game.resume();
      // The public table is the Craft & Trade stall by the pond, so Builder Workshop's gem has its space to itself.
      E.selectSlot(3);T.tp(-8.5,41,-14.5);ok(!E.placeBlock([-10,42,-16])&&!E.placeBlock([-11,41,-15]),'the Craft & Trade stall stays clear of builds');
      E.selectSlot(0);T.step(1);const prompt=()=>document.querySelector('.explore-prompt').textContent;
      ok(E.nearBench()&&E.world.get(-10,41,-16)===B.CRAFTING_TABLE&&prompt().includes('Craft & trade'),'the stall by the pond is a working crafting table');
      T.tp(.5,41,-13.5);ok(!E.nearBench()&&E.world.get(-1,42,-15)===0&&prompt().includes('Learn at Builder Workshop'),'the Builder Workshop gem no longer shares its space with a table');
      E.journal();document.querySelector('.journal-panel [data-act="findTable"]').click();T.step(1);
      ok(!game.paused&&E.waypoint?.x===-10&&E.waypoint?.z===-16&&E.trail.visible,'Map → Crafting table leads to the stall');E.waypoint=null;
      // Stronger tools materially change the timed interaction, not just the item picture.
      d.inventory.items.pick_stone=1;d.inventory.items.pick_iron=1;d.inventory.items.axe_diamond=1;
      assignSlot(d,'axe_diamond',1);E.selectSlot(1);T.tp(12.5,41,-19.5);T.step(10);E.beginGather([10,43,-20]);T.step(38);
      ok(E.world.get(10,43,-20)===0,'diamond axe gathers a log in well under a second');
      assignSlot(d,'pick_iron',2);E.selectSlot(2);T.tp(13.5,41,-19.5);T.step(10);
      ok(E.beginGather([13,41,-22]),'upgraded pickaxe can mine the previously locked ore');T.step(125);ok(amount(d,'ore')===2,'iron ore gives raw iron for the furnace');
      E.selectSlot(0);const animal=[...E.wildlife.animals.values()].find(a=>a.kind==='sheep'&&a.rx===0&&a.rz===-1);
      T.tp(animal.obj.position.x,animal.obj.position.y,animal.obj.position.z-2);const wool=amount(d,'wool');
      E.wildlife.hit(animal);ok(animal.hp===2&&amount(d,'wool')===wool,'wooden sword takes several hits to hunt an animal');T.step(25);E.wildlife.hit(animal);T.step(25);E.wildlife.hit(animal);
      ok(!animal.obj.visible&&amount(d,'wool')===wool+3&&amount(d,'food')===1,'animal poofs into collectable wool and food without blood');
      const count=amount(d,'wool');ok(!E.wildlife.hit(animal)&&amount(d,'wool')===count,'a defeated animal cannot pay out twice');
      assignSlot(d,'food',7);E.selectSlot(7);E.useSelected();ok(E.player.power.speed===30&&amount(d,'food')===0&&!E.character.item,'last food is consumed and no longer shown in the hand');
      const mined=d.harvested.map(b=>b.slice());const savedItems=JSON.stringify(Object.fromEntries(Object.entries(d.inventory.items).filter(([,n])=>n>0)));
      await game.ui.act('modeHome',{});ok(game.state==='menu'&&!game.explore&&!document.querySelector('#explore-hud'),'one tap returns from Explore to the mode selector');
      await game.startExplore();E=game.explore;d=E.data;T.step(5);
      ok(JSON.stringify(d.inventory.items)===savedItems,'inventory survives leaving and re-entering the mode');
      ok(E.world.get(...mined[0].slice(0,3))===0,'gathered tree blocks stay removed on re-entry');
      const sheep=[...E.wildlife.animals.values()].find(a=>a.id===animal.id);ok(sheep&&!sheep.obj.visible,'animal cooldown survives mode switching');
      ok(E.world.get(2,41,5)===B.CRAFTING_TABLE,'a crafted and placed table survives re-entry');
      E.data.worldTime+=245;E.regrow();E.wildlife.update(0);ok(E.world.get(...mined[0].slice(0,3))===mined[0][3]&&sheep.obj.visible,'resources and animals renew after active play time');
      E.openInventory('projects');ok(!E.claimProject('shelter'),'unfinished projects cannot award treasure');game.resume();
      // A town build and demolition survive streaming, mode changes and restored saves.
      E.selectSlot(3);T.tp(21.5,41,7.5);T.step(5);ok(E.placeBlock([22,41,8]),'building works around town outside the original plot');
      E.selectSlot(0);ok(E.beginGather([22,41,8]),'a sword can break a player-built block');T.step(260);ok(E.world.get(22,41,8)===0,'sword demolition returns the exact placed block to inventory');
      T.tp(-14.5,41,12.5);T.step(5);E.selectSlot(0);const wall=E.world.get(-15,41,16);
      ok(!!wall&&E.beginGather([-15,41,16]),'town buildings can be demolished with a sword');T.step(260);
      ok(E.world.get(-15,41,16)===0&&E.data.removed.some(b=>b[0]===-15&&b[2]===16),'town demolition records a permanent world edit');
      await game.startExplore();E=game.explore;d=E.data;T.step(5);ok(E.world.get(-15,41,16)===0,'demolished town block stays removed on re-entry');
      T.tp(80,41,5);T.step(8);const mob=[...E.enemies.values()].find(m=>m.kind==='zombie');
      ok(!!mob,'wilderness includes hostile monsters');
      const hp=E.player.hearts;E.player.pos.copy(mob.home);E.player.vel.set(0,0,0);E.player.invuln=0;mob.body.pos.copy(E.player.pos);mob.body.vel.set(0,0,0);mob.stun=0;mob.update(1/60);
      ok(E.player.hearts===hp-1&&d.hearts===hp-1,'monster contact consumes a heart and persists health');
      const itemsBefore=JSON.stringify(d.inventory.items);E.player.hearts=1;E.player.invuln=0;E.player.pos.copy(mob.home);E.player.vel.set(0,0,0);mob.body.pos.copy(E.player.pos);mob.body.vel.set(0,0,0);mob.update(1/60);
      ok(E.player.hearts===5&&Math.abs(E.player.pos.z+3.5)<.1,'empty hearts return Sai to town with full health');
      ok(JSON.stringify(d.inventory.items)===itemsBefore,'respawn keeps all inventory items');
      E.player.hearts=2;d.hearts=2;d.inventory.items.food=1;assignSlot(d,'food',7);E.selectSlot(7);E.useSelected();ok(E.player.hearts===4&&d.hearts===4,'food restores exactly two hearts');
      E.player.hearts=3;d.hearts=3;await game.startExplore();E=game.explore;d=E.data;ok(E.player.hearts===3,'health persists when changing modes');
      E.goHome();ok(E.player.hearts===5,'Town is an explicit safe heal and return');
      await T.startLevel('1-1');T.step(5);ok(!!document.querySelector('.mode-home'),'Adventure has a labelled home-screen button');
      const adventureMob=game.level.mobs[0];if(adventureMob){adventureMob.hp=10;adventureMob.body.pos.copy(game.level.player.pos).add(new THREE.Vector3(0,0,1));game.level.player.facing=0;game.save.data.equip.sword='diamond';game.level.swordHit(game.level.player);ok(adventureMob.hp===5,'diamond sword deals its advertised five damage in Adventure');}
      await game.ui.act('modeHome',{});ok(game.state==='menu'&&!game.level,'Adventure can return directly to the mode selector');
      await game.startBlitz('tables');T.step(3);ok(!!document.querySelector('.blitz-home'),'Brain Blitz has a direct home-screen button');
      await game.ui.act('modeHome',{});ok(game.state==='menu'&&!game.blitz,'Brain Blitz can return directly to the mode selector');
    } finally {game.showMenu();game.save.data=previous;game.save.persist();game.ui.showMain();}
    rep.errors=T.errors.slice();return rep;
  };

  // ---------- shared worlds ----------
  T.loginAs = async (name, mmyy, opts = {}) => {
    const r = await identity.login({ name, mmyy, ...opts });
    if (!r.ok) return r;
    if (!(await game.accounts.adopt(r))) return { ok: false, error: 'pull' };
    return { ok: true, ...identity.data };
  };
  T.joinRoom = async (code, ownerName) => {
    await game.startExplore({ room: code, ownerName });
    return game.state;
  };
  T.mpState = () => {
    const mp = game.mp;
    if (!mp) return { status: 'none' };
    return { mode: mp.mode, status: mp.status, count: mp.count, room: mp.room, you: mp.you, peers: mp.peers.list(), pending: mp.sync.pending?.length ?? null };
  };
  // Advance frames until a condition holds (network events arrive between steps).
  T.until = async (fn, { frames = 600, every = 5 } = {}) => {
    for (let i = 0; i < frames; i += every) {
      if (fn()) return true;
      T.step(every);
      await sleep(16);
    }
    return !!fn();
  };
  // Friends on the map (1.6): the initials and edge arrows on the minimap, and what the Map shows.
  T.minimap = () => {
    const marks = document.querySelector('#explore-hud .map-landmarks');
    const letters = (sel) => [...(marks?.querySelectorAll(sel) || [])].map((t) => t.textContent);
    return { near: letters('.map-friend text'), far: letters('.map-friend-far text') };
  };
  T.openMap = () => {
    game.explore.journal();
    const map = document.querySelector('#modal .explore-map');
    return {
      map: !!map,
      friends: [...(map?.querySelectorAll('[data-act="goToFriend"]') || [])].map((el) => el.dataset.id),
      buttons: [...document.querySelectorAll('#modal .journal-friends button')].map((b) => ({ text: b.textContent, id: b.dataset.id ?? null, disabled: b.disabled })),
    };
  };

  // ---------- phone HUD sheets (1.6) ----------
  // Open ☰ More or the guide card's sheet and list each button's action (the sheet stays open, paused).
  const sheetActs = (sel) => [...document.querySelectorAll(`#modal ${sel} [data-act]`)].map((b) => b.dataset.act + (b.dataset.op ? ':' + b.dataset.op : ''));
  T.moreSheet = () => {
    game.explore?.moreSheet();
    return sheetActs('.more-panel');
  };
  T.guideSheet = () => {
    game.explore?.guideSheet();
    return sheetActs('.guide-panel');
  };

  window.__test = T;
}
